import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { FiDownload, FiCalendar, FiTrendingUp, FiDollarSign, FiShoppingCart, FiUsers, FiFileText } from 'react-icons/fi';

// Helper function to get current month date range
const getCurrentMonthRange = () => {
  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  
  return {
    start: firstDay.toISOString().split('T')[0],
    end: lastDay.toISOString().split('T')[0]
  };
};

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState(getCurrentMonthRange());
  const [reportType, setReportType] = useState('sales');
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]);
  const [categories, setCategories] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [reportData, setReportData] = useState({
    totalSales: 0,
    totalInvoices: 0,
    completedSales: 0,
    completedInvoices: 0,
    voidedInvoices: 0,
    avgInvoiceValue: 0,
    topCustomers: [],
    salesByDay: {},
    salesByStatus: {}
  });
  const [customerReportData, setCustomerReportData] = useState({
    totalCustomers: 0,
    activeCustomers: 0,
    newCustomers: 0,
    returningCustomers: 0,
    avgLifetimeValue: 0,
    avgRevenuePerCustomer: 0,
    topCustomers: [],
    repeatPurchaseRate: 0,
    customerGrowth: {}
  });
  const [inventoryReportData, setInventoryReportData] = useState({
    totalInventoryValue: 0,
    totalUnitsInStock: 0,
    stockByCategory: [],
    inventoryTurnoverRate: 0,
    lowStockItems: [],
    outOfStockItems: [],
    fastMovingItems: [],
    slowMovingItems: [],
    topSellingProducts: [],
    stockAging: {},
    avgDaysInInventory: 0
  });
  const [employeeReportData, setEmployeeReportData] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    onLeaveEmployees: 0,
    newHires: 0,
    employeesByRole: [],
    employeesByStatus: {},
    avgTenure: 0,
    roleDistribution: [],
    topPerformers: [],
    recentHires: [],
    totalPayroll: 0,
    avgSalary: 0
  });
  const { selectedHierarchy } = useAuth();

  useEffect(() => {
    // Subscribe to invoices for real-time updates
    const unsubscribeInvoices = subscribeToInvoices();
    
    // Load customers for customer reports
    let unsubscribeCustomers = null;
    if (reportType === 'customers') {
      loadCustomers().then(unsub => {
        unsubscribeCustomers = unsub;
      });
    }
    
    // Load inventory data for inventory reports
    let unsubscribeProducts = null;
    let unsubscribeStock = null;
    let unsubscribeCategories = null;
    if (reportType === 'inventory') {
      loadInventoryData().then(unsubs => {
        if (unsubs) {
          unsubscribeProducts = unsubs.products;
          unsubscribeStock = unsubs.stock;
          unsubscribeCategories = unsubs.categories;
        }
      });
    }
    
    // Load employee data for employee reports
    let unsubscribeEmployees = null;
    let unsubscribeRoles = null;
    if (reportType === 'employees') {
      loadEmployeeData().then(unsubs => {
        if (unsubs) {
          unsubscribeEmployees = unsubs.employees;
          unsubscribeRoles = unsubs.roles;
        }
      });
    }
    
    return () => {
      if (unsubscribeInvoices) unsubscribeInvoices();
      if (unsubscribeCustomers) unsubscribeCustomers();
      if (unsubscribeProducts) unsubscribeProducts();
      if (unsubscribeStock) unsubscribeStock();
      if (unsubscribeCategories) unsubscribeCategories();
      if (unsubscribeEmployees) unsubscribeEmployees();
      if (unsubscribeRoles) unsubscribeRoles();
    };
  }, [selectedHierarchy, reportType]);

  useEffect(() => {
    // Calculate report data when invoices or date range changes
    if (reportType === 'sales') {
      calculateReportData();
    }
    
    // Recalculate customer report data when customers, invoices or date range changes
    if (reportType === 'customers' && customers.length > 0) {
      calculateCustomerReportData();
    }
    
    // Recalculate inventory report data when products, stock or date range changes
    if (reportType === 'inventory' && products.length > 0) {
      calculateInventoryReportData();
    }
    
    // Recalculate employee report data when employees or date range changes
    if (reportType === 'employees' && employees.length > 0) {
      calculateEmployeeReportData();
    }
  }, [invoices, dateRange, reportType, customers, products, stock, employees]);

  const subscribeToInvoices = () => {
    if (!selectedHierarchy?.outlet?.id) {
      setLoading(false);
      return null;
    }

    setLoading(true);
    const invoicesRef = collection(db, 'invoices');

    try {
      const q = query(
        invoicesRef,
        where('outletId', '==', selectedHierarchy.outlet.id)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const invoicesData = snapshot.docs.map(doc => {
            const data = doc.data();
            return {
              id: doc.id,
              ...data,
              createdAt: data.timestamp || data.createdAt,
              total: data.totalAmount || data.total || 0,
            };
          });

          setInvoices(invoicesData);
          setLoading(false);
        },
        (error) => {
          setLoading(false);
        }
      );

      return unsubscribe;
    } catch (error) {
      setLoading(false);
      return null;
    }
  };

  const loadCustomers = async () => {
    if (!selectedHierarchy?.outlet?.id) {
      console.log('No outlet selected');
      return null;
    }
    
    console.log('Loading customers for outlet:', selectedHierarchy.outlet.id);
    
    try {
      const customersQuery = query(
        collection(db, 'customers'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      
      const unsubscribe = onSnapshot(customersQuery, (snapshot) => {
        const customersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Loaded customers:', customersData.length);
        
        // If no customers found with outlet filter, check if there are any customers at all
        if (customersData.length === 0) {
          // Check total customers without filter for debugging
          const allCustomersRef = collection(db, 'customers');
          onSnapshot(allCustomersRef, (allSnapshot) => {
            console.log('Total customers in database:', allSnapshot.docs.length);
            if (allSnapshot.docs.length > 0) {
              console.log('Sample customer data:', allSnapshot.docs[0].data());
            }
          }, { once: true });
        }
        
        setCustomers(customersData);
        // Don't calculate here, let useEffect handle it when both customers and invoices are ready
      });
      
      return unsubscribe;
    } catch (error) {
      console.error('Error loading customers:', error);
      return null;
    }
  };

  const calculateCustomerReportData = (customersData = customers) => {
    console.log('Calculating customer report...', {
      customersCount: customersData?.length,
      invoicesCount: invoices.length,
      dateRange
    });
    
    if (!customersData || customersData.length === 0) {
      console.log('No customers data available');
      setCustomerReportData({
        totalCustomers: 0,
        activeCustomers: 0,
        newCustomers: 0,
        returningCustomers: 0,
        avgLifetimeValue: 0,
        avgRevenuePerCustomer: 0,
        topCustomers: [],
        repeatPurchaseRate: 0,
        customerGrowth: {}
      });
      return;
    }

    // Filter invoices by date range
    let filteredInvoices = [...invoices];
    if (dateRange.start || dateRange.end) {
      filteredInvoices = filteredInvoices.filter(inv => {
        const invDate = getDateFromTimestamp(inv.createdAt);
        if (!invDate) return false;
        const startMatch = !dateRange.start || invDate >= new Date(dateRange.start);
        const endMatch = !dateRange.end || invDate <= new Date(dateRange.end + 'T23:59:59');
        return startMatch && endMatch;
      });
    }

    // Calculate customer metrics
    const totalCustomers = customersData.length;
    const activeCustomers = customersData.filter(c => c.status === 'active').length;
    
    // Calculate new customers (created in date range)
    const newCustomers = customersData.filter(c => {
      const createdDate = getDateFromTimestamp(c.createdAt);
      if (!createdDate) return false;
      const inRange = (!dateRange.start || createdDate >= new Date(dateRange.start)) &&
                      (!dateRange.end || createdDate <= new Date(dateRange.end + 'T23:59:59'));
      return inRange;
    }).length;

    // Calculate revenue per customer
    const customerRevenue = {};
    const customerPurchaseCount = {};
    
    filteredInvoices.forEach(inv => {
      const customerId = inv.customerId || inv.customerName || 'Walk-in';
      const amount = Number(inv.total) || 0;
      
      if (!customerRevenue[customerId]) {
        customerRevenue[customerId] = 0;
        customerPurchaseCount[customerId] = 0;
      }
      
      if (inv.status?.toLowerCase() !== 'void' && inv.status?.toLowerCase() !== 'voided') {
        customerRevenue[customerId] += amount;
        customerPurchaseCount[customerId]++;
      }
    });

    // Calculate top customers
    const topCustomers = Object.entries(customerRevenue)
      .map(([customerId, revenue]) => {
        const customer = customersData.find(c => c.id === customerId);
        return {
          id: customerId,
          name: customer?.name || customer?.customerName || customerId,
          revenue: revenue,
          purchaseCount: customerPurchaseCount[customerId] || 0,
          avgOrderValue: revenue / (customerPurchaseCount[customerId] || 1)
        };
      })
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);

    // Calculate average revenue per customer
    const totalRevenue = Object.values(customerRevenue).reduce((sum, rev) => sum + rev, 0);
    const customersWithPurchases = Object.keys(customerRevenue).length;
    const avgRevenuePerCustomer = customersWithPurchases > 0 ? totalRevenue / customersWithPurchases : 0;
    const avgLifetimeValue = totalCustomers > 0 ? totalRevenue / totalCustomers : 0;

    // Calculate repeat purchase rate
    const customersWithMultiplePurchases = Object.values(customerPurchaseCount).filter(count => count > 1).length;
    const repeatPurchaseRate = customersWithPurchases > 0 
      ? (customersWithMultiplePurchases / customersWithPurchases) * 100 
      : 0;

    // Calculate returning customers (customers with more than 1 purchase)
    const returningCustomers = customersWithMultiplePurchases;

    // Calculate customer growth over time
    const customerGrowth = {};
    customersData.forEach(customer => {
      const date = getDateFromTimestamp(customer.createdAt);
      if (date) {
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        if (!customerGrowth[monthKey]) {
          customerGrowth[monthKey] = 0;
        }
        customerGrowth[monthKey]++;
      }
    });

    const result = {
      totalCustomers,
      activeCustomers,
      newCustomers,
      returningCustomers,
      avgLifetimeValue,
      avgRevenuePerCustomer,
      topCustomers,
      repeatPurchaseRate,
      customerGrowth
    };
    
    console.log('Customer report calculated:', result);
    setCustomerReportData(result);
  };

  const loadInventoryData = async () => {
    if (!selectedHierarchy?.outlet?.id) return null;
    
    console.log('Loading inventory data for outlet:', selectedHierarchy.outlet.id);
    
    try {
      // Load products
      const productsQuery = query(
        collection(db, 'products'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const unsubProducts = onSnapshot(productsQuery, (snapshot) => {
        const productsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Loaded products:', productsData.length);
        setProducts(productsData);
      });

      // Load stock
      const stockQuery1 = query(
        collection(db, 'stock'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const stockQuery2 = query(
        collection(db, 'stock'),
        where('outletId', '==', null)
      );
      
      const unsubStock = onSnapshot(stockQuery1, (snapshot1) => {
        onSnapshot(stockQuery2, (snapshot2) => {
          const stockData = [
            ...snapshot1.docs.map(doc => ({ id: doc.id, ...doc.data() })),
            ...snapshot2.docs.map(doc => ({ id: doc.id, ...doc.data() }))
          ];
          console.log('Loaded stock items:', stockData.length);
          setStock(stockData);
        });
      });

      // Load categories
      const categoriesQuery = query(
        collection(db, 'categories'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const unsubCategories = onSnapshot(categoriesQuery, (snapshot) => {
        const categoriesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Loaded categories:', categoriesData.length);
        setCategories(categoriesData);
      });

      return {
        products: unsubProducts,
        stock: unsubStock,
        categories: unsubCategories
      };
    } catch (error) {
      console.error('Error loading inventory data:', error);
      return null;
    }
  };

  const calculateInventoryReportData = () => {
    console.log('Calculating inventory report...', {
      productsCount: products.length,
      stockCount: stock.length,
      categoriesCount: categories.length
    });

    if (!products || products.length === 0) {
      console.log('No products data available');
      setInventoryReportData({
        totalInventoryValue: 0,
        totalUnitsInStock: 0,
        stockByCategory: [],
        inventoryTurnoverRate: 0,
        lowStockItems: [],
        outOfStockItems: [],
        fastMovingItems: [],
        slowMovingItems: [],
        topSellingProducts: [],
        stockAging: {},
        avgDaysInInventory: 0
      });
      return;
    }

    // Create product-stock map
    const productStockMap = {};
    stock.forEach(s => {
      productStockMap[s.productId] = s.quantity || 0;
    });

    // Calculate total inventory value and units
    let totalInventoryValue = 0;
    let totalUnitsInStock = 0;
    const categoryStockMap = {};

    products.forEach(product => {
      const stockQty = productStockMap[product.id] || 0;
      const value = stockQty * (Number(product.sellingPrice) || 0);
      
      totalInventoryValue += value;
      totalUnitsInStock += stockQty;

      // Group by category
      const categoryName = categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized';
      if (!categoryStockMap[categoryName]) {
        categoryStockMap[categoryName] = { value: 0, units: 0, products: 0 };
      }
      categoryStockMap[categoryName].value += value;
      categoryStockMap[categoryName].units += stockQty;
      categoryStockMap[categoryName].products++;
    });

    // Convert to array and sort
    const stockByCategory = Object.entries(categoryStockMap)
      .map(([name, data]) => ({ name, ...data }))
      .sort((a, b) => b.value - a.value);

    // Find low stock and out of stock items
    const lowStockItems = [];
    const outOfStockItems = [];
    
    products.forEach(product => {
      const stockQty = productStockMap[product.id] || 0;
      const reorderLevel = Number(product.reorderLevel) || 10;
      
      if (stockQty === 0) {
        outOfStockItems.push({
          id: product.id,
          name: product.name,
          category: categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized',
          stock: 0,
          reorderLevel
        });
      } else if (stockQty <= reorderLevel) {
        lowStockItems.push({
          id: product.id,
          name: product.name,
          category: categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized',
          stock: stockQty,
          reorderLevel
        });
      }
    });

    // Calculate product sales from invoices
    const productSales = {};
    invoices.forEach(inv => {
      if (inv.status?.toLowerCase() !== 'void' && inv.status?.toLowerCase() !== 'voided') {
        (inv.items || []).forEach(item => {
          const productId = item.productId || item.id;
          if (!productSales[productId]) {
            productSales[productId] = { quantity: 0, revenue: 0 };
          }
          productSales[productId].quantity += Number(item.quantity) || 0;
          productSales[productId].revenue += (Number(item.quantity) || 0) * (Number(item.price) || 0);
        });
      }
    });

    // Top selling products
    const topSellingProducts = Object.entries(productSales)
      .map(([productId, data]) => {
        const product = products.find(p => p.id === productId);
        return {
          id: productId,
          name: product?.name || 'Unknown Product',
          quantity: data.quantity,
          revenue: data.revenue,
          stock: productStockMap[productId] || 0
        };
      })
      .sort((a, b) => b.quantity - a.quantity)
      .slice(0, 10);

    // Fast moving vs slow moving (based on sales velocity)
    const productsWithSales = products.map(product => {
      const sales = productSales[product.id];
      const stockQty = productStockMap[product.id] || 0;
      return {
        id: product.id,
        name: product.name,
        category: categories.find(c => c.id === product.categoryId)?.name || 'Uncategorized',
        stock: stockQty,
        salesQty: sales?.quantity || 0,
        revenue: sales?.revenue || 0,
        turnoverRate: stockQty > 0 && sales?.quantity > 0 ? (sales.quantity / stockQty) : 0
      };
    });

    const fastMovingItems = productsWithSales
      .filter(p => p.salesQty > 0)
      .sort((a, b) => b.turnoverRate - a.turnoverRate)
      .slice(0, 10);

    const slowMovingItems = productsWithSales
      .filter(p => p.stock > 0 && p.salesQty === 0)
      .sort((a, b) => b.stock - a.stock)
      .slice(0, 10);

    // Calculate average inventory turnover rate
    const totalSales = Object.values(productSales).reduce((sum, data) => sum + data.quantity, 0);
    const inventoryTurnoverRate = totalUnitsInStock > 0 ? (totalSales / totalUnitsInStock) : 0;

    const result = {
      totalInventoryValue,
      totalUnitsInStock,
      stockByCategory,
      inventoryTurnoverRate,
      lowStockItems: lowStockItems.slice(0, 10),
      outOfStockItems: outOfStockItems.slice(0, 10),
      fastMovingItems,
      slowMovingItems,
      topSellingProducts,
      stockAging: {},
      avgDaysInInventory: inventoryTurnoverRate > 0 ? (365 / inventoryTurnoverRate) : 0
    };

    console.log('Inventory report calculated:', result);
    setInventoryReportData(result);
  };

  const loadEmployeeData = async () => {
    if (!selectedHierarchy?.outlet?.id) return null;
    
    console.log('Loading employee data for outlet:', selectedHierarchy.outlet.id);
    
    try {
      // Load employees
      const employeesQuery = query(
        collection(db, 'employees'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const unsubEmployees = onSnapshot(employeesQuery, (snapshot) => {
        const employeesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Loaded employees:', employeesData.length);
        setEmployees(employeesData);
      });

      // Load roles
      const rolesQuery = query(
        collection(db, 'roles'),
        where('outletId', '==', selectedHierarchy.outlet.id)
      );
      const unsubRoles = onSnapshot(rolesQuery, (snapshot) => {
        const rolesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        console.log('Loaded roles:', rolesData.length);
        setRoles(rolesData);
      });

      return {
        employees: unsubEmployees,
        roles: unsubRoles
      };
    } catch (error) {
      console.error('Error loading employee data:', error);
      return null;
    }
  };

  const calculateEmployeeReportData = () => {
    console.log('Calculating employee report...', {
      employeesCount: employees.length,
      rolesCount: roles.length
    });

    if (!employees || employees.length === 0) {
      console.log('No employees data available');
      setEmployeeReportData({
        totalEmployees: 0,
        activeEmployees: 0,
        onLeaveEmployees: 0,
        newHires: 0,
        employeesByRole: [],
        employeesByStatus: {},
        avgTenure: 0,
        roleDistribution: [],
        topPerformers: [],
        recentHires: [],
        totalPayroll: 0,
        avgSalary: 0
      });
      return;
    }

    // Filter employees by date range for new hires
    let newHires = 0;
    if (dateRange.start || dateRange.end) {
      newHires = employees.filter(emp => {
        const hireDate = emp.hireDate ? new Date(emp.hireDate) : emp.createdAt ? getDateFromTimestamp(emp.createdAt) : null;
        if (!hireDate) return false;
        const startMatch = !dateRange.start || hireDate >= new Date(dateRange.start);
        const endMatch = !dateRange.end || hireDate <= new Date(dateRange.end + 'T23:59:59');
        return startMatch && endMatch;
      }).length;
    }

    // Count by status
    const totalEmployees = employees.length;
    const activeEmployees = employees.filter(e => e.status === 'active' || !e.status).length;
    const onLeaveEmployees = employees.filter(e => e.status === 'on leave').length;

    // Group by role
    const roleMap = {};
    employees.forEach(emp => {
      const roleIds = emp.roleIds || [];
      roleIds.forEach(roleId => {
        const role = roles.find(r => r.id === roleId);
        const roleName = role?.name || 'Unassigned';
        if (!roleMap[roleName]) {
          roleMap[roleName] = 0;
        }
        roleMap[roleName]++;
      });
    });

    const roleDistribution = Object.entries(roleMap)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);

    // Status distribution
    const employeesByStatus = {};
    employees.forEach(emp => {
      const status = emp.status || 'active';
      if (!employeesByStatus[status]) {
        employeesByStatus[status] = 0;
      }
      employeesByStatus[status]++;
    });

    // Calculate payroll
    const totalPayroll = employees.reduce((sum, emp) => sum + (Number(emp.salary) || 0), 0);
    const avgSalary = totalEmployees > 0 ? totalPayroll / totalEmployees : 0;

    // Calculate average tenure
    const now = new Date();
    let totalTenureDays = 0;
    let employeesWithHireDate = 0;
    
    employees.forEach(emp => {
      const hireDate = emp.hireDate ? new Date(emp.hireDate) : emp.createdAt ? getDateFromTimestamp(emp.createdAt) : null;
      if (hireDate) {
        const tenureDays = Math.floor((now - hireDate) / (1000 * 60 * 60 * 24));
        totalTenureDays += tenureDays;
        employeesWithHireDate++;
      }
    });
    
    const avgTenure = employeesWithHireDate > 0 ? Math.floor(totalTenureDays / employeesWithHireDate / 30) : 0; // in months

    // Recent hires (last 30 days)
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    const recentHires = employees
      .filter(emp => {
        const hireDate = emp.hireDate ? new Date(emp.hireDate) : emp.createdAt ? getDateFromTimestamp(emp.createdAt) : null;
        return hireDate && hireDate >= thirtyDaysAgo;
      })
      .map(emp => ({
        name: emp.name,
        email: emp.email,
        roleIds: emp.roleIds || [],
        hireDate: emp.hireDate || emp.createdAt,
        status: emp.status
      }))
      .sort((a, b) => {
        const dateA = a.hireDate ? new Date(a.hireDate) : new Date();
        const dateB = b.hireDate ? new Date(b.hireDate) : new Date();
        return dateB - dateA;
      })
      .slice(0, 10);

    const result = {
      totalEmployees,
      activeEmployees,
      onLeaveEmployees,
      newHires,
      employeesByRole: roleDistribution,
      employeesByStatus,
      avgTenure,
      roleDistribution,
      topPerformers: [],
      recentHires,
      totalPayroll,
      avgSalary
    };

    console.log('Employee report calculated:', result);
    setEmployeeReportData(result);
  };

  const calculateReportData = () => {
    let filteredInvoices = [...invoices];

    // Filter by date range if specified
    if (dateRange.start || dateRange.end) {
      filteredInvoices = filteredInvoices.filter(inv => {
        const invDate = getDateFromTimestamp(inv.createdAt);
        if (!invDate) return false;

        const startMatch = !dateRange.start || invDate >= new Date(dateRange.start);
        const endMatch = !dateRange.end || invDate <= new Date(dateRange.end + 'T23:59:59');
        return startMatch && endMatch;
      });
    }

    // Calculate metrics
    const completed = filteredInvoices.filter(inv => 
      inv.status?.toLowerCase() === 'completed' || inv.status?.toLowerCase() === 'paid'
    );
    const voided = filteredInvoices.filter(inv => 
      inv.status?.toLowerCase() === 'void' || inv.status?.toLowerCase() === 'voided'
    );
    const nonVoided = filteredInvoices.filter(inv => 
      inv.status?.toLowerCase() !== 'void' && inv.status?.toLowerCase() !== 'voided'
    );

    const totalSales = nonVoided.reduce((sum, inv) => sum + (Number(inv.total) || 0), 0);
    const completedSales = completed.reduce((sum, inv) => sum + (Number(inv.total) || 0), 0);
    const avgInvoiceValue = nonVoided.length > 0 ? totalSales / nonVoided.length : 0;

    // Sales by status
    const salesByStatus = {};
    filteredInvoices.forEach(inv => {
      const status = inv.status?.toLowerCase() || 'completed';
      if (!salesByStatus[status]) {
        salesByStatus[status] = { count: 0, amount: 0 };
      }
      salesByStatus[status].count++;
      salesByStatus[status].amount += Number(inv.total) || 0;
    });

    // Sales by day
    const salesByDay = {};
    nonVoided.forEach(inv => {
      const date = getDateFromTimestamp(inv.createdAt);
      if (date) {
        const dateKey = date.toISOString().split('T')[0];
        if (!salesByDay[dateKey]) {
          salesByDay[dateKey] = { count: 0, amount: 0 };
        }
        salesByDay[dateKey].count++;
        salesByDay[dateKey].amount += Number(inv.total) || 0;
      }
    });

    // Top customers by sales
    const customerSales = {};
    nonVoided.forEach(inv => {
      const customerId = inv.customerId || inv.customerName || 'Walk-in';
      if (!customerSales[customerId]) {
        customerSales[customerId] = { name: inv.customerName || customerId, amount: 0, count: 0 };
      }
      customerSales[customerId].amount += Number(inv.total) || 0;
      customerSales[customerId].count++;
    });

    const topCustomers = Object.values(customerSales)
      .sort((a, b) => b.amount - a.amount)
      .slice(0, 10);

    setReportData({
      totalSales,
      totalInvoices: filteredInvoices.length,
      completedSales,
      completedInvoices: completed.length,
      voidedInvoices: voided.length,
      avgInvoiceValue,
      topCustomers,
      salesByDay,
      salesByStatus
    });
  };

  const getDateFromTimestamp = (timestamp) => {
    if (!timestamp) return null;
    try {
      if (timestamp.toDate && typeof timestamp.toDate === 'function') {
        return timestamp.toDate();
      }
      if (timestamp instanceof Date) {
        return timestamp;
      }
      if (typeof timestamp === 'string') {
        return new Date(timestamp);
      }
      if (typeof timestamp === 'number') {
        return new Date(timestamp);
      }
    } catch (error) {
      return null;
    }
    return null;
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return `$${Number(amount).toFixed(2)}`;
  };

  const handleExport = (format) => {
    alert(`Exporting report as ${format}. This feature will be implemented soon.`);
  };

  return (
    <div className="container-fluid">
      <div className="page-header mb-4">
        <h1 className="page-title">Reports & Analytics</h1>
        <p className="page-subtitle">View business insights and performance metrics</p>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Report Type</label>
              <select 
                className="form-select" 
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
              >
                <option value="sales">Sales Report</option>
                <option value="inventory">Inventory Report</option>
                <option value="customers">Customer Report</option>
                <option value="employees">Employee Report</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-control"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
              />
            </div>
            <div className="col-md-3">
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-control"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
              />
            </div>
            <div className="col-md-3 d-flex align-items-end gap-2">
              <button 
                className="btn btn-outline-primary flex-grow-1"
                onClick={() => {
                  setDateRange({ start: '', end: '' });
                }}
              >
                Clear Filters
              </button>
              <div className="btn-group">
                <button 
                  className="btn btn-outline-secondary dropdown-toggle" 
                  data-bs-toggle="dropdown"
                >
                  <FiDownload />
                </button>
                <ul className="dropdown-menu">
                  <li><button className="dropdown-item" onClick={() => handleExport('PDF')}>Export as PDF</button></li>
                  <li><button className="dropdown-item" onClick={() => handleExport('Excel')}>Export as Excel</button></li>
                  <li><button className="dropdown-item" onClick={() => handleExport('CSV')}>Export as CSV</button></li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Render different content based on report type */}
      {reportType === 'sales' && (
        <>
          {/* Key Metrics - Based on Invoices */}
          <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Total Sales</p>
                  <h3 className="mb-0">{formatCurrency(reportData.totalSales)}</h3>
                  <small className="text-muted">{reportData.totalInvoices} invoices</small>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--success-gradient)', width: '48px', height: '48px' }}>
                  <FiDollarSign className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Completed Sales</p>
                  <h3 className="mb-0">{formatCurrency(reportData.completedSales)}</h3>
                  <small className="text-muted">{reportData.completedInvoices} invoices</small>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--primary-gradient)', width: '48px', height: '48px' }}>
                  <FiFileText className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Average Invoice</p>
                  <h3 className="mb-0">{formatCurrency(reportData.avgInvoiceValue)}</h3>
                  <small className="text-muted">Per transaction</small>
                </div>
                <div className="stat-card-icon" style={{ background: 'var(--info-gradient)', width: '48px', height: '48px' }}>
                  <FiTrendingUp className="text-white" size={24} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card hover-lift">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <p className="text-muted mb-1 small">Voided Invoices</p>
                  <h3 className="mb-0">{reportData.voidedInvoices}</h3>
                  <small className="text-muted">Cancelled transactions</small>
                </div>
                <div className="stat-card-icon" style={{ background: 'linear-gradient(135deg, #6c757d 0%, #495057 100%)', width: '48px', height: '48px' }}>
                  <i className="bi bi-x-circle text-white" style={{ fontSize: '24px' }}></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sales by Status */}
      <div className="row g-3 mb-4">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Sales by Status</h5>
            </div>
            <div className="card-body">
              {Object.keys(reportData.salesByStatus).length === 0 ? (
                <p className="text-muted text-center py-4">No data available</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th className="text-end">Count</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(reportData.salesByStatus)
                        .sort((a, b) => b[1].amount - a[1].amount)
                        .map(([status, data]) => (
                          <tr key={status}>
                            <td className="text-capitalize">{status}</td>
                            <td className="text-end">{data.count}</td>
                            <td className="text-end fw-semibold">{formatCurrency(data.amount)}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Top Customers */}
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Top Customers</h5>
            </div>
            <div className="card-body">
              {reportData.topCustomers.length === 0 ? (
                <p className="text-muted text-center py-4">No customer data available</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>Customer</th>
                        <th className="text-end">Invoices</th>
                        <th className="text-end">Total Sales</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.topCustomers.map((customer, index) => (
                        <tr key={index}>
                          <td>{customer.name}</td>
                          <td className="text-end">{customer.count}</td>
                          <td className="text-end fw-semibold">{formatCurrency(customer.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Sales by Day */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Daily Sales Summary</h5>
        </div>
        <div className="card-body">
          {Object.keys(reportData.salesByDay).length === 0 ? (
            <p className="text-muted text-center py-4">No sales data available for the selected period</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th className="text-end">Invoices</th>
                    <th className="text-end">Total Sales</th>
                    <th className="text-end">Avg Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(reportData.salesByDay)
                    .sort((a, b) => b[0].localeCompare(a[0]))
                    .map(([date, data]) => (
                      <tr key={date}>
                        <td>{new Date(date).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}</td>
                        <td className="text-end">{data.count}</td>
                        <td className="text-end fw-semibold">{formatCurrency(data.amount)}</td>
                        <td className="text-end">{formatCurrency(data.amount / data.count)}</td>
                      </tr>
                    ))}
                  <tr className="table-active fw-bold">
                    <td>Total</td>
                    <td className="text-end">
                      {Object.values(reportData.salesByDay).reduce((sum, d) => sum + d.count, 0)}
                    </td>
                    <td className="text-end">
                      {formatCurrency(Object.values(reportData.salesByDay).reduce((sum, d) => sum + d.amount, 0))}
                    </td>
                    <td className="text-end">-</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
        </>
      )}

      {/* Inventory Report */}
      {reportType === 'inventory' && (
        <>
          {products.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <FiShoppingCart size={64} className="text-muted mb-3" />
                <h5 className="mb-3">No Inventory Data Available</h5>
                <p className="text-muted">
                  {loading ? 'Loading inventory data...' : 'No products found for this outlet.'}
                </p>
              </div>
            </div>
          ) : (
            <>
          {/* 1. Inventory Overview */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">🧭 Inventory Overview</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded bg-success bg-opacity-10">
                    <h3 className="mb-1 text-success">{formatCurrency(inventoryReportData.totalInventoryValue)}</h3>
                    <small className="text-muted">Total Inventory Value</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded bg-primary bg-opacity-10">
                    <h3 className="mb-1 text-primary">{inventoryReportData.totalUnitsInStock.toLocaleString()}</h3>
                    <small className="text-muted">Total Units in Stock</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded bg-info bg-opacity-10">
                    <h3 className="mb-1 text-info">{inventoryReportData.inventoryTurnoverRate.toFixed(2)}x</h3>
                    <small className="text-muted">Inventory Turnover Rate</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded bg-warning bg-opacity-10">
                    <h3 className="mb-1 text-warning">{Math.round(inventoryReportData.avgDaysInInventory)}</h3>
                    <small className="text-muted">Avg Days in Inventory</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stock by Category */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">📊 Stock by Category</h5>
            </div>
            <div className="card-body">
              {inventoryReportData.stockByCategory.length === 0 ? (
                <p className="text-muted text-center py-3">No category data available</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th className="text-end">Products</th>
                        <th className="text-end">Units</th>
                        <th className="text-end">Value</th>
                        <th>Stock Level</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventoryReportData.stockByCategory.map((cat, index) => {
                        const percentage = (cat.value / inventoryReportData.totalInventoryValue) * 100;
                        return (
                          <tr key={index}>
                            <td className="fw-semibold">{cat.name}</td>
                            <td className="text-end">{cat.products}</td>
                            <td className="text-end">{cat.units.toLocaleString()}</td>
                            <td className="text-end fw-bold text-success">{formatCurrency(cat.value)}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div className="progress flex-grow-1" style={{ height: '8px' }}>
                                  <div 
                                    className="progress-bar bg-success" 
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <small className="text-muted">{percentage.toFixed(1)}%</small>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Stock Alerts */}
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <div className="card border-danger">
                <div className="card-header bg-danger bg-opacity-10">
                  <h6 className="mb-0 text-danger">🚨 Out of Stock Items ({inventoryReportData.outOfStockItems.length})</h6>
                </div>
                <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {inventoryReportData.outOfStockItems.length === 0 ? (
                    <p className="text-muted text-center py-3">No out of stock items</p>
                  ) : (
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Category</th>
                          <th className="text-end">Reorder Level</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryReportData.outOfStockItems.map((item, index) => (
                          <tr key={index}>
                            <td className="fw-semibold">{item.name}</td>
                            <td><span className="badge bg-secondary">{item.category}</span></td>
                            <td className="text-end">{item.reorderLevel}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card border-warning">
                <div className="card-header bg-warning bg-opacity-10">
                  <h6 className="mb-0 text-warning">⚠️ Low Stock Items ({inventoryReportData.lowStockItems.length})</h6>
                </div>
                <div className="card-body" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {inventoryReportData.lowStockItems.length === 0 ? (
                    <p className="text-muted text-center py-3">No low stock items</p>
                  ) : (
                    <table className="table table-sm mb-0">
                      <thead>
                        <tr>
                          <th>Product</th>
                          <th>Category</th>
                          <th className="text-end">Stock</th>
                          <th className="text-end">Reorder At</th>
                        </tr>
                      </thead>
                      <tbody>
                        {inventoryReportData.lowStockItems.map((item, index) => (
                          <tr key={index}>
                            <td className="fw-semibold">{item.name}</td>
                            <td><span className="badge bg-secondary">{item.category}</span></td>
                            <td className="text-end text-warning fw-bold">{item.stock}</td>
                            <td className="text-end">{item.reorderLevel}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Product Performance */}
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header bg-success bg-opacity-10">
                  <h6 className="mb-0">🔥 Fast-Moving Items (Top 10)</h6>
                </div>
                <div className="card-body">
                  {inventoryReportData.fastMovingItems.length === 0 ? (
                    <p className="text-muted text-center py-3">No sales data available</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Product</th>
                            <th className="text-end">Sold</th>
                            <th className="text-end">Stock</th>
                            <th className="text-end">Turnover</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventoryReportData.fastMovingItems.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td className="fw-semibold">{item.name}</td>
                              <td className="text-end">{item.salesQty}</td>
                              <td className="text-end">{item.stock}</td>
                              <td className="text-end">
                                <span className="badge bg-success">{item.turnoverRate.toFixed(2)}x</span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="card">
                <div className="card-header bg-secondary bg-opacity-10">
                  <h6 className="mb-0">🐌 Slow-Moving Items (Top 10)</h6>
                </div>
                <div className="card-body">
                  {inventoryReportData.slowMovingItems.length === 0 ? (
                    <p className="text-muted text-center py-3">All items are selling</p>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Product</th>
                            <th>Category</th>
                            <th className="text-end">Stock</th>
                            <th className="text-end">Sales</th>
                          </tr>
                        </thead>
                        <tbody>
                          {inventoryReportData.slowMovingItems.map((item, index) => (
                            <tr key={index}>
                              <td>{index + 1}</td>
                              <td className="fw-semibold">{item.name}</td>
                              <td><span className="badge bg-secondary">{item.category}</span></td>
                              <td className="text-end text-warning">{item.stock}</td>
                              <td className="text-end text-muted">{item.salesQty}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Top Selling Products */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">🏆 Top 10 Best-Selling Products</h5>
            </div>
            <div className="card-body">
              {inventoryReportData.topSellingProducts.length === 0 ? (
                <p className="text-muted text-center py-4">No sales data available</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Rank</th>
                        <th>Product Name</th>
                        <th className="text-end">Quantity Sold</th>
                        <th className="text-end">Revenue Generated</th>
                        <th className="text-end">Current Stock</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inventoryReportData.topSellingProducts.map((product, index) => (
                        <tr key={index}>
                          <td>
                            <span className={`badge ${index < 3 ? 'bg-warning' : 'bg-secondary'}`}>
                              #{index + 1}
                            </span>
                          </td>
                          <td className="fw-semibold">{product.name}</td>
                          <td className="text-end fw-bold">{product.quantity.toLocaleString()}</td>
                          <td className="text-end text-success fw-bold">{formatCurrency(product.revenue)}</td>
                          <td className="text-end">{product.stock.toLocaleString()}</td>
                          <td>
                            {product.stock === 0 ? (
                              <span className="badge bg-danger">Out of Stock</span>
                            ) : product.stock < 10 ? (
                              <span className="badge bg-warning">Low Stock</span>
                            ) : (
                              <span className="badge bg-success">In Stock</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* Key Insights */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">💡 Key Inventory Insights</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 border rounded">
                    <h6 className="mb-3">Replenishment Alerts</h6>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <span className="badge bg-danger me-2">{inventoryReportData.outOfStockItems.length}</span>
                        <small>Items are completely out of stock</small>
                      </li>
                      <li className="mb-2">
                        <span className="badge bg-warning me-2">{inventoryReportData.lowStockItems.length}</span>
                        <small>Items are below reorder level</small>
                      </li>
                      <li>
                        <span className="badge bg-info me-2">{inventoryReportData.slowMovingItems.length}</span>
                        <small>Items are slow-moving or obsolete</small>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 border rounded">
                    <h6 className="mb-3">Performance Metrics</h6>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <FiTrendingUp className="text-success me-2" />
                        <small>
                          Inventory turns over <strong>{inventoryReportData.inventoryTurnoverRate.toFixed(2)}</strong> times
                        </small>
                      </li>
                      <li className="mb-2">
                        <FiCalendar className="text-info me-2" />
                        <small>
                          Average <strong>{Math.round(inventoryReportData.avgDaysInInventory)}</strong> days to sell inventory
                        </small>
                      </li>
                      <li>
                        <FiDollarSign className="text-warning me-2" />
                        <small>
                          Total capital locked: <strong>{formatCurrency(inventoryReportData.totalInventoryValue)}</strong>
                        </small>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </>
          )}
        </>
      )}

      {/* Customer Report */}
      {reportType === 'customers' && (
        <>
          {customers.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <FiUsers size={64} className="text-muted mb-3" />
                <h5 className="mb-3">No Customer Data Available</h5>
                <p className="text-muted mb-4">
                  {loading 
                    ? 'Loading customer data...' 
                    : 'No customers found for the selected outlet.'}
                </p>
                {!loading && (
                  <div className="alert alert-info mx-auto" style={{ maxWidth: '600px' }}>
                    <h6 className="alert-heading">💡 How to add customers:</h6>
                    <ul className="text-start mb-0">
                      <li>Customers are automatically created when you create invoices/orders from the POS terminal</li>
                      <li>You can also manually add customers from the Customers page</li>
                      <li>Make sure customers have the correct <code>outletId</code> field set</li>
                    </ul>
                    <hr />
                    <small className="text-muted">
                      <strong>Debug Info:</strong> Selected Outlet ID: <code>{selectedHierarchy?.outlet?.id || 'None'}</code>
                      <br />
                      Check browser console for more details.
                    </small>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <>
          {/* 1. Customer Overview */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">🧩 Customer Overview</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <h3 className="mb-1 text-primary">{customerReportData.totalCustomers}</h3>
                    <small className="text-muted">Total Customers</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <h3 className="mb-1 text-success">{customerReportData.activeCustomers}</h3>
                    <small className="text-muted">Active Customers</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <h3 className="mb-1 text-info">{customerReportData.newCustomers}</h3>
                    <small className="text-muted">New Customers</small>
                    <div className="text-muted" style={{ fontSize: '10px' }}>
                      (in selected period)
                    </div>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded">
                    <h3 className="mb-1 text-warning">{customerReportData.returningCustomers}</h3>
                    <small className="text-muted">Returning Customers</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Revenue & Value Analytics */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">💰 Revenue & Value Analytics</h5>
            </div>
            <div className="card-body">
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded bg-light">
                    <h4 className="mb-1 text-success">{formatCurrency(customerReportData.avgLifetimeValue)}</h4>
                    <small className="text-muted">Customer Lifetime Value (CLV)</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded bg-light">
                    <h4 className="mb-1 text-primary">{formatCurrency(customerReportData.avgRevenuePerCustomer)}</h4>
                    <small className="text-muted">Average Revenue Per Customer (ARPU)</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded bg-light">
                    <h4 className="mb-1 text-info">{customerReportData.repeatPurchaseRate.toFixed(1)}%</h4>
                    <small className="text-muted">Repeat Purchase Rate</small>
                  </div>
                </div>
              </div>

              {/* Top 10 High-Value Customers */}
              <h6 className="mt-4 mb-3">Top 10 High-Value Customers</h6>
              {customerReportData.topCustomers.length === 0 ? (
                <p className="text-muted text-center py-3">No customer data available</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-sm">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Customer Name</th>
                        <th className="text-end">Total Revenue</th>
                        <th className="text-end">Purchase Count</th>
                        <th className="text-end">Avg Order Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {customerReportData.topCustomers.map((customer, index) => (
                        <tr key={customer.id}>
                          <td>{index + 1}</td>
                          <td className="fw-semibold">{customer.name}</td>
                          <td className="text-end text-success fw-bold">{formatCurrency(customer.revenue)}</td>
                          <td className="text-end">{customer.purchaseCount}</td>
                          <td className="text-end">{formatCurrency(customer.avgOrderValue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* 3. Acquisition & Retention */}
          <div className="row g-3 mb-4">
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">🕓 Acquisition & Retention</h5>
                </div>
                <div className="card-body">
                  <div className="row g-3">
                    <div className="col-6">
                      <div className="text-center p-3 border rounded">
                        <h4 className="mb-1 text-info">{customerReportData.newCustomers}</h4>
                        <small className="text-muted">New Customers</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="text-center p-3 border rounded">
                        <h4 className="mb-1 text-success">{customerReportData.returningCustomers}</h4>
                        <small className="text-muted">Returning Customers</small>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 p-3 border rounded bg-light">
                    <div className="d-flex justify-content-between align-items-center">
                      <span className="text-muted">Customer Retention Rate</span>
                      <strong className="text-success">
                        {customerReportData.totalCustomers > 0 
                          ? ((customerReportData.returningCustomers / customerReportData.totalCustomers) * 100).toFixed(1)
                          : 0}%
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="card">
                <div className="card-header">
                  <h5 className="mb-0">💬 Engagement Metrics</h5>
                </div>
                <div className="card-body">
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">Active Customers</span>
                      <strong>{customerReportData.activeCustomers}</strong>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div 
                        className="progress-bar bg-success" 
                        style={{ 
                          width: `${customerReportData.totalCustomers > 0 
                            ? (customerReportData.activeCustomers / customerReportData.totalCustomers) * 100 
                            : 0}%` 
                        }}
                      ></div>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">Repeat Purchase Rate</span>
                      <strong>{customerReportData.repeatPurchaseRate.toFixed(1)}%</strong>
                    </div>
                    <div className="progress" style={{ height: '8px' }}>
                      <div 
                        className="progress-bar bg-info" 
                        style={{ width: `${customerReportData.repeatPurchaseRate}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="p-3 border rounded bg-light mt-3">
                    <small className="text-muted">
                      <FiUsers className="me-2" />
                      {customerReportData.returningCustomers} out of {customerReportData.totalCustomers} customers made repeat purchases
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 4. Customer Growth Over Time */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">📈 Customer Growth Over Time</h5>
            </div>
            <div className="card-body">
              {Object.keys(customerReportData.customerGrowth).length === 0 ? (
                <p className="text-muted text-center py-4">No growth data available</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover table-sm">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th className="text-end">New Customers</th>
                        <th className="text-end">Growth</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(customerReportData.customerGrowth)
                        .sort((a, b) => b[0].localeCompare(a[0]))
                        .map(([month, count], index, array) => {
                          const prevCount = index < array.length - 1 ? array[index + 1][1] : 0;
                          const growth = prevCount > 0 ? ((count - prevCount) / prevCount * 100).toFixed(1) : 0;
                          return (
                            <tr key={month}>
                              <td>
                                {new Date(month + '-01').toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'long' 
                                })}
                              </td>
                              <td className="text-end fw-semibold">{count}</td>
                              <td className="text-end">
                                <span className={`badge bg-${growth >= 0 ? 'success' : 'danger'}`}>
                                  {growth > 0 ? '+' : ''}{growth}%
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* 5. Customer Insights Summary */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">🧠 Customer Insights & Segmentation</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 border rounded">
                    <h6 className="mb-3">Customer Value Segmentation</h6>
                    <div className="mb-2">
                      <div className="d-flex justify-content-between mb-1">
                        <small>High-Value (Top 10%)</small>
                        <small className="fw-bold">{Math.ceil(customerReportData.totalCustomers * 0.1)}</small>
                      </div>
                      <div className="progress" style={{ height: '6px' }}>
                        <div className="progress-bar bg-success" style={{ width: '10%' }}></div>
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="d-flex justify-content-between mb-1">
                        <small>Medium-Value (30%)</small>
                        <small className="fw-bold">{Math.ceil(customerReportData.totalCustomers * 0.3)}</small>
                      </div>
                      <div className="progress" style={{ height: '6px' }}>
                        <div className="progress-bar bg-info" style={{ width: '30%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="d-flex justify-content-between mb-1">
                        <small>Low-Value (60%)</small>
                        <small className="fw-bold">{Math.ceil(customerReportData.totalCustomers * 0.6)}</small>
                      </div>
                      <div className="progress" style={{ height: '6px' }}>
                        <div className="progress-bar bg-secondary" style={{ width: '60%' }}></div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 border rounded">
                    <h6 className="mb-3">Quick Insights</h6>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <FiTrendingUp className="text-success me-2" />
                        <small>
                          Top 10 customers generate <strong>{formatCurrency(customerReportData.topCustomers.slice(0, 10).reduce((sum, c) => sum + c.revenue, 0))}</strong> in revenue
                        </small>
                      </li>
                      <li className="mb-2">
                        <FiUsers className="text-info me-2" />
                        <small>
                          <strong>{customerReportData.repeatPurchaseRate.toFixed(0)}%</strong> of customers make repeat purchases
                        </small>
                      </li>
                      <li>
                        <FiDollarSign className="text-warning me-2" />
                        <small>
                          Average customer lifetime value: <strong>{formatCurrency(customerReportData.avgLifetimeValue)}</strong>
                        </small>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </>
          )}
        </>
      )}

      {/* Employee Report */}
      {reportType === 'employees' && (
        <>
          {employees.length === 0 ? (
            <div className="card">
              <div className="card-body text-center py-5">
                <FiUsers size={64} className="text-muted mb-3" />
                <h5 className="mb-3">No Employee Data Available</h5>
                <p className="text-muted">
                  {loading ? 'Loading employee data...' : 'No employees found for this outlet.'}
                </p>
              </div>
            </div>
          ) : (
            <>
          {/* 1. Employee Overview */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">👥 Employee Overview</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded bg-primary bg-opacity-10">
                    <h3 className="mb-1 text-primary">{employeeReportData.totalEmployees}</h3>
                    <small className="text-muted">Total Employees</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded bg-success bg-opacity-10">
                    <h3 className="mb-1 text-success">{employeeReportData.activeEmployees}</h3>
                    <small className="text-muted">Active Employees</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded bg-warning bg-opacity-10">
                    <h3 className="mb-1 text-warning">{employeeReportData.onLeaveEmployees}</h3>
                    <small className="text-muted">On Leave</small>
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="text-center p-3 border rounded bg-info bg-opacity-10">
                    <h3 className="mb-1 text-info">{employeeReportData.newHires}</h3>
                    <small className="text-muted">New Hires (Period)</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 2. Role Distribution */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">💼 Role Distribution</h5>
            </div>
            <div className="card-body">
              {employeeReportData.roleDistribution.length === 0 ? (
                <p className="text-muted text-center py-3">No role data available</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Role</th>
                        <th className="text-end">Employees</th>
                        <th>Distribution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeReportData.roleDistribution.map((role, index) => {
                        const percentage = (role.count / employeeReportData.totalEmployees) * 100;
                        return (
                          <tr key={index}>
                            <td className="fw-semibold">{role.name}</td>
                            <td className="text-end">{role.count}</td>
                            <td>
                              <div className="d-flex align-items-center gap-2">
                                <div className="progress flex-grow-1" style={{ height: '8px' }}>
                                  <div 
                                    className="progress-bar bg-primary" 
                                    style={{ width: `${percentage}%` }}
                                  ></div>
                                </div>
                                <small className="text-muted">{percentage.toFixed(1)}%</small>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* 3. Status Distribution */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">📊 Employee Status</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                {Object.entries(employeeReportData.employeesByStatus).map(([status, count]) => {
                  const percentage = (count / employeeReportData.totalEmployees) * 100;
                  const badgeColor = status === 'active' ? 'success' : 
                                    status === 'on leave' ? 'warning' : 
                                    status === 'terminated' ? 'danger' : 'secondary';
                  return (
                    <div key={status} className="col-md-4">
                      <div className={`p-3 border rounded bg-${badgeColor} bg-opacity-10`}>
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className={`badge bg-${badgeColor}`}>{status.toUpperCase()}</span>
                          <h4 className={`mb-0 text-${badgeColor}`}>{count}</h4>
                        </div>
                        <div className="progress" style={{ height: '6px' }}>
                          <div 
                            className={`progress-bar bg-${badgeColor}`}
                            style={{ width: `${percentage}%` }}
                          ></div>
                        </div>
                        <small className="text-muted mt-1 d-block">{percentage.toFixed(1)}% of total</small>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* 4. Compensation Analytics */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">💰 Compensation Analytics</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded bg-success bg-opacity-10">
                    <h4 className="mb-1 text-success">{formatCurrency(employeeReportData.totalPayroll)}</h4>
                    <small className="text-muted">Total Payroll</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded bg-info bg-opacity-10">
                    <h4 className="mb-1 text-info">{formatCurrency(employeeReportData.avgSalary)}</h4>
                    <small className="text-muted">Average Salary</small>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="text-center p-3 border rounded bg-warning bg-opacity-10">
                    <h4 className="mb-1 text-warning">{employeeReportData.avgTenure} mo</h4>
                    <small className="text-muted">Average Tenure</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* 5. Recent Hires */}
          <div className="card mb-4">
            <div className="card-header">
              <h5 className="mb-0">🆕 Recent Hires (Last 30 Days)</h5>
            </div>
            <div className="card-body">
              {employeeReportData.recentHires.length === 0 ? (
                <p className="text-muted text-center py-3">No recent hires in the last 30 days</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Roles</th>
                        <th>Hire Date</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employeeReportData.recentHires.map((emp, index) => {
                        const hireDate = emp.hireDate ? new Date(emp.hireDate) : new Date();
                        return (
                          <tr key={index}>
                            <td className="fw-semibold">{emp.name}</td>
                            <td>{emp.email}</td>
                            <td>
                              {emp.roleIds && emp.roleIds.length > 0 ? (
                                emp.roleIds.map(roleId => {
                                  const role = roles.find(r => r.id === roleId);
                                  return role ? (
                                    <span key={roleId} className="badge bg-primary me-1">
                                      {role.name}
                                    </span>
                                  ) : null;
                                })
                              ) : (
                                <span className="badge bg-secondary">No Role</span>
                              )}
                            </td>
                            <td>{hireDate.toLocaleDateString()}</td>
                            <td>
                              <span className={`badge ${emp.status === 'active' ? 'bg-success' : 'bg-warning'}`}>
                                {emp.status || 'active'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          {/* 6. Key HR Insights */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">💡 Key HR Insights</h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="p-3 border rounded">
                    <h6 className="mb-3">Workforce Composition</h6>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <span className="badge bg-primary me-2">{employeeReportData.totalEmployees}</span>
                        <small>Total workforce size</small>
                      </li>
                      <li className="mb-2">
                        <span className="badge bg-success me-2">{employeeReportData.activeEmployees}</span>
                        <small>Currently active employees</small>
                      </li>
                      <li className="mb-2">
                        <span className="badge bg-info me-2">{employeeReportData.roleDistribution.length}</span>
                        <small>Different job roles</small>
                      </li>
                      <li>
                        <span className="badge bg-warning me-2">{employeeReportData.avgTenure}</span>
                        <small>Average tenure in months</small>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="p-3 border rounded">
                    <h6 className="mb-3">Performance Highlights</h6>
                    <ul className="list-unstyled mb-0">
                      <li className="mb-2">
                        <FiTrendingUp className="text-success me-2" />
                        <small>
                          <strong>{employeeReportData.newHires}</strong> new hires in selected period
                        </small>
                      </li>
                      <li className="mb-2">
                        <FiDollarSign className="text-info me-2" />
                        <small>
                          Monthly payroll: <strong>{formatCurrency(employeeReportData.totalPayroll)}</strong>
                        </small>
                      </li>
                      <li className="mb-2">
                        <FiUsers className="text-primary me-2" />
                        <small>
                          Active workforce: <strong>{((employeeReportData.activeEmployees / employeeReportData.totalEmployees) * 100).toFixed(1)}%</strong>
                        </small>
                      </li>
                      <li>
                        <FiCalendar className="text-warning me-2" />
                        <small>
                          Average employee stays <strong>{employeeReportData.avgTenure}</strong> months
                        </small>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
            </>
          )}
        </>
      )}

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted mt-3">Loading report data...</p>
        </div>
      )}
    </div>
  );
}
